import { z } from 'zod';
import { statementSourceSchema } from './bank.js';
import { isoDate, uuid } from './posting.js';
/**
 * Die Verträge der Webhooks — Aufgabe 1 `[WH-schema]` des Plans
 * `docs/superpowers/plans/2026-09-10-webhooks-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-10-webhooks-design.md` (Abschnitte 5.1, 7.1
 * und 8).
 *
 * **Was hier steht: die Form, in der eine Nachricht das Haus verlässt.** Der
 * Ereigniskatalog, die Rumpfform des einen Ereignisses, der Endpunkt als
 * Auskunft und die Statusaufzählung der Zustellung. Was hier ausdrücklich
 * **nicht** steht: die Anfrageformen der Endpunkte aus Spec 7.3 — sie
 * entstehen mit `WH-oberflaeche` (Task 4), und ein Vertrag, den kein Endpunkt
 * liest, käme mangels Aufrufer ohnehin nicht in `docs/openapi/openapi.json`.
 *
 * **Der Ereigniskatalog steht GENAU EINMAL, und das ist hier** (`D-13`). Die
 * `CHECK`-Bedingung `webhook_endpoint_ereignis_bekannt` in Migration `0210`
 * schreibt dieselben Werte notwendigerweise ein zweites Mal hin — eine
 * Datenbank kann kein TypeScript-Schema lesen. Das ist die eine unvermeidliche
 * Dublette dieser Ära; wer den Katalog erweitert, ändert beide Orte im selben
 * Commit.
 *
 * **Beträge sind Zeichenketten, und hier sogar strenger als sonst**
 * (Hausregel 1). Anderswo in diesem Paket steht `centAmount`, das eine
 * Zeichenkette liest und ein `Money` zurückgibt. Hier steht `betragSchema`,
 * das eine Zeichenkette liest und eine **Zeichenkette** zurückgibt — denn
 * dieser Wert wird nicht gerechnet, sondern **gesendet**, und was gesendet
 * wird, muss byteweise bestimmt sein: `12.30` als JSON-Zahl wäre `12.3`, und
 * die Signatur des Empfängers ginge über eine andere Bytefolge als die, die
 * hier gemeint war (`E-1194`, `E-1195`). Ein `Money` ist zur Laufzeit
 * dieselbe Zeichenkette; was fehlte, wäre nicht der Wert, sondern die
 * Gewissheit, dass niemand ihn unterwegs durch eine Zahl schickt.
 *
 * **Der Rumpf trägt kein `kunde.name`** (Spec 5.1). Er stünde in
 * `bank_transaction.counterpartyName` und ist der Name des **Überweisenden**,
 * nicht zwingend der des Kunden. Ein Feld, das manchmal stimmt, ist schlechter
 * als keins.
 *
 * **Die IBAN ist maskiert und nicht weggelassen** (`D-4`). Der Empfänger soll
 * erkennen können, auf welches Konto gezahlt wurde, wenn er mehrere führt;
 * vollständig gehört sie nicht in eine Nachricht, die über `http://` laufen
 * darf, und die letzten vier Stellen unterscheiden zuverlässig, ohne jemanden
 * zu identifizieren.
 */
/**
 * Der Ereigniskatalog — **die eine Quelle** (`D-13`).
 *
 * Genau ein Wert. Der Mechanismus ist generisch, die Liste ist es nicht: Ein
 * Ereigniskatalog ist eine Schnittstellenzusage nach aussen, und jede Zeile
 * darin ist ab ihrer Veröffentlichung ein Vertrag, den jemand liest. Zwanzig
 * erfundene Ereignisse wären zwanzig Verträge ohne Leser — und der erste echte
 * Leser fände seine Form nicht darunter.
 *
 * `bank` steht im **Namen** und nicht als Feld, damit die Abgrenzung sichtbar
 * ist, bevor jemand den Rumpf liest (`D-17`): PayPal und Mollie lösen dieses
 * Ereignis **nie** aus — dafür gibt es einen direkten IPN, und ein zweiter
 * Anlass für dieselbe Gutschrift wäre eine Doppelbuchung drüben.
 */
export const webhookKatalogEreignisSchema = z.enum(['guthaben.aufladung.bank']);
/**
 * Das Ereignis eines Workflows (Spec Workflows `D-12`, Nachtrag 0.4 Nr. 2): `workflow.<schluessel>`,
 * geschrieben vom Knoten „Webhook senden" des Workflows mit diesem Schlüssel. Der Rumpf ist der,
 * den der Knoten baut — es gibt keinen veröffentlichten Vertrag je Workflow, die Felder stehen
 * in lexikographischer Folge (`kanonisch.ts`). Dasselbe Muster hält
 * `webhook_endpoint_ereignis_bekannt` (Migration `0355`).
 */
export const WORKFLOW_WEBHOOK_EREIGNIS_MUSTER = /^workflow\.[a-z0-9-]+$/;
export const workflowWebhookEreignisSchema = z
    .string()
    .regex(WORKFLOW_WEBHOOK_EREIGNIS_MUSTER, 'Ein Workflow-Ereignis heisst workflow.<schluessel>.');
/** Ein Ereignis eines Endpunkts: das Katalogereignis oder ein Workflow-Ereignis. */
export const webhookEreignisSchema = z.union([webhookKatalogEreignisSchema, workflowWebhookEreignisSchema]);
/**
 * Der Zustand einer Zustellung (`D-5`).
 *
 * `offen` → `zugestellt` oder `offen` → `erschoepft`, und aus `erschoepft`
 * genau ein Weg zurück nach `offen`: „Erneut senden". `zugestellt` ist
 * endgültig. Die Übergänge hält der Trigger `webhook_zustellung_uebergang()`
 * aus Migration `0211` (`E-1183`), nicht dieses Schema — hier steht der
 * Katalog.
 */
export const webhookZustellungStatusSchema = z.enum(['offen', 'zugestellt', 'erschoepft']);
/**
 * Der Kanal, aus dem das Geld kam (`D-17`, Spec 5.1, Feld `quelle`).
 *
 * `fints` und `upload` sind **beides „Bank"**: Eine camt-Datei, die ein Mensch
 * hochlädt, ist dasselbe Geld auf demselben Konto wie ein FinTS-Abruf, die
 * Aufladungsautomatik unterscheidet sie nicht, und diese Ära tut es auch
 * nicht. `paypal` und `mollie` stehen deshalb **nicht** hier — nicht als
 * verbotener Wert, sondern gar nicht: Ein Rumpf mit `"quelle": "paypal"` ist
 * kein zurückgewiesenes Ereignis, sondern eines, das es nicht gibt.
 *
 * **Woher der Wert kommt — und eine Berichtigung der Spec.** Spec 5.1 führt
 * `quelle` als `bank_account.provider`. Das ist am Schema gemessen **nicht
 * haltbar**: `bank_provider` ist ein Aufzählungstyp über
 * `('fints', 'paypal', 'mollie')` (Migration `0028`), und `upload` kommt darin
 * nicht vor — ein Konto wird nicht „hochgeladen", ein **Auszug** wird es.
 * `upload` lebt in `bank_statement_import.source`
 * (`statementSourceSchema`, `bank.ts`), und genau von dort kommt dieser Wert:
 * Er sagt, **auf welchem Weg die Bewegung eingelesen wurde**, und das ist
 * ohnehin die Aussage, die `D-17` treffen will. Task 2
 * (`WH-bankaufladung`) liest ihn deshalb über
 * `bank_transaction.import_id → bank_statement_import.source` und nicht über
 * `bank_account.provider`.
 *
 * **Abgeleitet und nicht abgeschrieben.** Die zwei Werte sind die Bank-Hälfte
 * von `statementSourceSchema`; sie hier als eigenes `z.enum` zu wiederholen
 * wäre die Fehlerklasse, die `pnpm api:enums:check` jagt — ein zweiter
 * Wertesatz, den ein neuer Kanal nicht mitnimmt. `extract` bricht statt zu
 * schweigen, wenn einer der beiden Namen dort einmal anders heisst.
 */
export const webhookBankquelleSchema = statementSourceSchema.extract(['fints', 'upload']);
/**
 * Ein Geldbetrag, wie er im Rumpf steht: eine Zeichenkette, die eine
 * Zeichenkette bleibt.
 *
 * Zwei Nachkommastellen, Punkt als Trenner, optional negativ — dieselbe Form,
 * in der PostgreSQL ein `numeric(15,2)` ausgibt und in der `suche.ts` seine
 * Beträge führt. **Bewusst kein `centAmount`:** Jenes gibt ein `Money` zurück,
 * und ein `Money` ist gedacht, um gerechnet zu werden. Hier wird nicht
 * gerechnet, hier wird gesendet.
 */
const betragSchema = z.string().regex(/^-?\d+\.\d{2}$/, 'Kein gültiger Betrag');
/**
 * Ein Zeitpunkt in ISO-8601, UTC, mit `Z` — die Form, in der jedes Datumsfeld
 * des Rumpfes steht (`D-4`).
 *
 * Das System rechnet intern durchgängig UTC (`TZ=UTC` im Manifest). Eine
 * Ortszeit im Rumpf wäre eine zweite Auslegung desselben Zeitpunkts, und der
 * Empfänger müsste raten, welche gilt.
 */
const utcZeitpunktSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/, 'Zeitpunkt in ISO-8601 UTC erwartet');
/**
 * Der Kunde, dem die Aufladung gutgeschrieben wurde.
 *
 * `nummer` ist die Zahl aus dem Verwendungszweck, plausibilitätsgeprüft
 * (`E-357`), und ist immer da — ohne sie hätte die Erkennung gar nicht
 * gegriffen. `id` ist die interne Kennung, **wo auflösbar, sonst `null`**: Der
 * Bankweg trägt keine `customer_id` auf der Buchung (`aufladung.ts`: „Keine
 * Kundenzuordnung auf keiner Zeile"), und ein Feld, das eine Auflösung
 * behauptet, die es nicht gab, wäre schlechter als ein `null`.
 */
export const webhookKundeSchema = z
    .object({
    nummer: z.string().min(1),
    id: uuid.nullable(),
})
    .strict();
/**
 * Das Geldkonto, auf dem das Geld ankam.
 *
 * `iban` ist **maskiert** (`D-4`) — die letzten vier Stellen sichtbar.
 * `nummer` ist die Sachkontonummer (`1200`), damit ein Empfänger, der die
 * Buchhaltung kennt, das Konto ohne die IBAN zuordnen kann.
 */
export const webhookBankkontoSchema = z
    .object({
    iban: z.string().min(1),
    nummer: z.string().min(1),
})
    .strict();
/**
 * Der Rumpf des Ereignisses `guthaben.aufladung.bank`, vollständig (Spec 5.1).
 *
 * **Die Reihenfolge dieser Felder IST der Vertrag.** Sie steht hier so, wie
 * die Spec sie festlegt, und `kanonischerRumpf()` (Task 3,
 * `apps/api/src/webhooks/kanonisch.ts`) schreibt sie beim Bauen der Bytefolge
 * ein zweites Mal aus — als **Liste**, nicht über `Object.keys()`. Der Grund
 * gehört hierher, weil er zuerst hier sichtbar wird: Die Schlüsselreihenfolge
 * eines JavaScript-Objekts ist zwar festgelegt, hängt aber an der Reihenfolge
 * der Zuweisungen irgendwo weiter oben — und das ist keine Zusage, die man
 * einem Empfänger machen will, dessen Signaturprüfung über die rohen Bytes
 * rechnet.
 *
 * **`.strict()`**, damit ein zusätzliches Feld ein Fehler ist und nicht
 * stillschweigend mitreist: Was der Empfänger sieht, soll das sein, was hier
 * steht (`E-1185`).
 *
 * **`id` und `journalEntryId` tragen dasselbe, und das ist Absicht**
 * (Spec 5.2). `id` ist der Vertrag („dedupliziere hierauf"), `journalEntryId`
 * die Auskunft („so heisst die Buchung bei uns, falls du fragst"). Änderte
 * sich eines Tages die Wahl der Kennung, änderte sich `id` und nicht
 * `journalEntryId` — genau deshalb sind es zwei Felder und nicht eins.
 *
 * **`erzeugtAm` ist die Entstehungszeit der BUCHUNG und nicht die Uhr des
 * Zustellers.** Sonst wäre der Rumpf je Versuch ein anderer und die Signatur
 * nicht wiederholbar.
 */
export const guthabenAufladungBankRumpfSchema = z
    .object({
    /** Die Ereignis-Kennung: `journal_entry.id` (`D-3`). */
    id: uuid,
    /** Der Ereignisname aus dem Katalog. */
    ereignis: z.literal(webhookKatalogEreignisSchema.enum['guthaben.aufladung.bank']),
    /** `journal_entry.created_at`, ISO-8601 UTC. */
    erzeugtAm: utcZeitpunktSchema,
    kunde: webhookKundeSchema,
    /** Der Bruttobetrag der `1796`-Zeile. Auf dem Bankkanal ist brutto gleich netto. */
    betrag: betragSchema,
    /** Immer `EUR` — die Aufladungsregel lässt nichts anderes zu. */
    waehrung: z.literal('EUR'),
    /** `bank_transaction.value_date`; `null`, wo der Auszug keine führt. */
    wertstellung: isoDate.nullable(),
    /** `journal_entry.booking_date`. */
    buchungsdatum: isoDate,
    /** `bank_transaction.purpose`, **im Original** — ungekürzt, unnormalisiert. */
    verwendungszweck: z.string(),
    bankkonto: webhookBankkontoSchema,
    /** `bank_transaction.id` — für Rückfragen, **nicht** für Deduplikation. */
    bankTransactionId: uuid,
    /** `journal_entry.id`, dasselbe wie `id` (Spec 5.2). */
    journalEntryId: uuid,
    /**
     * Auf welchem Weg die Bewegung eingelesen wurde (`D-17`) — gelesen aus
     * `bank_statement_import.source` und **nicht** aus
     * `bank_account.provider`; die Begründung steht am Schema oben.
     */
    quelle: webhookBankquelleSchema,
})
    .strict();
/**
 * Die Feldreihenfolge des Rumpfes, maschinenlesbar.
 *
 * **Warum sie neben dem Schema noch einmal als Liste steht.** Zod hält die
 * Form, nicht die Reihenfolge — `safeParse` gibt ein Objekt zurück, dessen
 * Schlüsselreihenfolge von der Reihenfolge der Eingabe abhängen kann. Der
 * kanonische Text (`D-4`) hängt aber an genau dieser Reihenfolge, und eine
 * Zusage, die nur in der Reihenfolge von Quelltextzeilen lebt, lässt sich
 * nicht prüfen. Diese Liste lässt sich prüfen (`E-1185`), und
 * `kanonischerRumpf()` (Task 3) baut den Text daraus.
 */
export const GUTHABEN_AUFLADUNG_BANK_FELDER = [
    'id',
    'ereignis',
    'erzeugtAm',
    'kunde',
    'betrag',
    'waehrung',
    'wertstellung',
    'buchungsdatum',
    'verwendungszweck',
    'bankkonto',
    'bankTransactionId',
    'journalEntryId',
    'quelle',
];
/**
 * Ein Webhook-Endpunkt, wie ihn eine Auskunft ausgibt (Spec 7.1).
 *
 * **Ohne Secret, und ohne ein Feld, das eines tragen könnte.** Es gibt keinen
 * Endpunkt, der ein gespeichertes Secret zurückgibt — auch nicht für den
 * Administrator, auch nicht mit einer zweiten Fähigkeit (Spec 7.2). Was jede
 * Antwort trägt, ist `secretGesetztAm`; der Klartext erscheint **genau einmal**
 * in der Antwort auf das Anlegen und auf die Rotation, und dieses Schema ist
 * nicht der Ort dafür.
 *
 * **Kein Löschzeitpunkt und kein `geloescht`-Feld.** Ein Endpunkt wird
 * deaktiviert, nie gelöscht (`D-14`): Sonst verlöre das Zustellungsprotokoll
 * seinen Bezug.
 */
export const webhookEndpunktSchema = z
    .object({
    id: uuid,
    name: z.string().min(1),
    ereignis: webhookEreignisSchema,
    url: z.string().url(),
    aktiv: z.boolean(),
    secretGesetztAm: utcZeitpunktSchema,
    angelegtAm: utcZeitpunktSchema,
    geaendertAm: utcZeitpunktSchema.nullable(),
})
    .strict();
/**
 * Eine Zustellung, wie sie das Protokoll ausgibt (Spec 7.3, 8.2).
 *
 * **`versuch` zählt die ABGESCHLOSSENEN Versuche** und ist damit `0` an einer
 * frisch angelegten Zeile; der Kopf `X-Fame-Attempt` ist demgegenüber
 * 1-basiert und nennt den laufenden (Spec 5.5).
 *
 * **`versuchBasis` (Migration `0221`, Auftrag `WH-deckelneu`) ist der Stand
 * von `versuch`, ab dem der AKTUELLE Zyklus zählt** — `0` an einer Zeile,
 * die noch nie „erneut gesendet" wurde, sonst der `versuch`-Stand zum
 * Zeitpunkt des letzten Klicks. `versuch` selbst fällt nie und bleibt die
 * Geschichte der Zeile (Spec 8.3); `versuchBasis` ist der verschiebbare
 * Nullpunkt, den die Fälligkeitsrechnung (`backoff.ts`) von ihm abzieht.
 *
 * **`httpStatus` ist `null` bei Verbindungsfehler, Zeitablauf und
 * TLS-Fehler** — dort gibt es keinen Status. `0` wäre ein Status, den HTTP
 * nicht kennt, und jede Auswertung müsste ihn eigens ausnehmen.
 */
export const webhookZustellungSchema = z
    .object({
    id: uuid,
    endpointId: uuid,
    ereignis: z.string().min(1),
    /** Die Ereignis-Kennung (`D-3`) — der Wert, an dem der Empfänger dedupliziert. */
    ereignisId: uuid,
    status: webhookZustellungStatusSchema,
    versuch: z.number().int().nonnegative(),
    versuchBasis: z.number().int().nonnegative(),
    naechsterVersuchAm: utcZeitpunktSchema.nullable(),
    letzterVersuchAm: utcZeitpunktSchema.nullable(),
    httpStatus: z.number().int().nullable(),
    /** Höchstens 1.000 Zeichen (`D-10`), und niemals das Secret (`E-1202`). */
    antwortAuszug: z.string().max(1000).nullable(),
    dauerMs: z.number().int().nonnegative().nullable(),
    zugestelltAm: utcZeitpunktSchema.nullable(),
    istTest: z.boolean(),
    erzeugtAm: utcZeitpunktSchema,
})
    .strict();
/* ==========================================================================
 * Die Anfrage- und Antwortformen der Verwaltung — Aufgabe 4
 * `[WH-oberflaeche]`, Spec Abschnitt 7.1 bis 7.3, `D-6`, `D-7`, `D-8`.
 *
 * **Was hier NEU hinzukommt und warum es nicht schon oben stand.** Der
 * Kopfkommentar dieser Datei sagt es ausdrücklich: Die Anfrageformen aus
 * Spec 7.3 entstehen mit Task 4, weil ein Vertrag ohne Aufrufer mangels
 * Endpunkt gar nicht erst in `docs/openapi/openapi.json` erschiene. Jetzt
 * gibt es die Endpunkte, also gibt es die Formen.
 *
 * **Das Secret hat GENAU EINE Antwortform, und sie heisst so**
 * ({@link webhookEndpunktMitSecretSchema}). Es gibt keine zweite, in der es
 * freiwillig mitkäme — ein `secret: z.string().optional()` an
 * {@link webhookEndpunktSchema} wäre die bequeme Variante und genau die
 * Fehlerklasse, gegen die `E-1206` existiert: Ein Feld, das manchmal da ist,
 * ist ein Feld, das eines Tages überall da ist. Die Trennung in zwei Typen
 * macht aus „das Secret steht in dieser Antwort" eine Aussage, die der
 * Übersetzer prüft.
 * ========================================================================== */
/**
 * Der Rumpf, mit dem ein Administrator einen Endpunkt anlegt (Spec 7.1).
 *
 * **Kein `secret` im Rumpf** (`D-6`). Es wird vom System erzeugt; ein Feld
 * dafür wäre die Einladung, ein Kennwort einzutippen, und Kennwörter dieser
 * Art sind kurz.
 *
 * **Kein `aktiv` beim Anlegen.** Ein Endpunkt, der abgeschaltet zur Welt
 * kommt, ist kein Fall, den jemand braucht — wer ihn nicht will, legt ihn
 * nicht an. Der Weg zu `aktiv: false` führt über
 * {@link webhookEndpunktPatchSchema}, und das ist genau ein Weg statt zwei.
 *
 * **Die URL wird hier NICHT auf ihre Zieltauglichkeit geprüft.**
 * `z.string().url()` sagt „das ist eine URL" und nichts über private
 * Adressbereiche; die Adressprüfung ist `pruefeZiel()`
 * (`apps/api/src/webhooks/ziel.ts`) und gehört in den Dienst, weil sie einen
 * Grund beim Namen nennt und nicht bloss „ungültig" — dieselbe Trennung wie
 * zwischen `isoDate` und der Periodenprüfung.
 */
export const webhookEndpunktAnlageSchema = z
    .object({
    name: z.string().trim().min(1, 'Name fehlt').max(200),
    ereignis: webhookEreignisSchema,
    url: z.string().url('Keine gültige Adresse'),
})
    .strict();
/**
 * Der Rumpf, mit dem ein Administrator einen Endpunkt ändert (Spec 7.1).
 *
 * **`ereignis` fehlt hier, und das ist keine Auslassung.** Der
 * Unveränderlichkeitstrigger `webhook_endpoint_immutable()` aus Migration
 * `0210` lässt genau `name`, `url`, `secret_verschluesselt`,
 * `secret_gesetzt_am`, `aktiv` und das Änderungspaar durch; ein `UPDATE` auf
 * `ereignis` wäre ein `restrict_violation` aus der Datenbank. Ein Feld
 * anzubieten, das die Ablage zurückweist, wäre ein Versprechen, das erst der
 * Server bricht. Wer ein anderes Ereignis will, legt einen zweiten Endpunkt
 * an — mehrere je Ereignis sind ausdrücklich erlaubt (Spec 7.1 `[A]`).
 *
 * **`aktiv: false` IST das Deaktivieren** und es gibt keinen eigenen Endpunkt
 * dafür. Ein `POST …/deaktivieren` neben einem `PATCH`, der dasselbe kann,
 * wären zwei Wege zu einem Zustand — und einer davon fiele beim nächsten
 * Umbau hinten runter. **Ein Löschweg existiert nicht** (`D-14`).
 *
 * **Alle drei Felder freiwillig, aber nicht alle drei weglassbar:** Ein
 * leerer Rumpf ändert nichts und wird vom Dienst abgewiesen, nicht hier — der
 * Vertrag beschreibt die Form, nicht die Sinnhaftigkeit.
 */
export const webhookEndpunktPatchSchema = z
    .object({
    name: z.string().trim().min(1, 'Name fehlt').max(200).optional(),
    url: z.string().url('Keine gültige Adresse').optional(),
    aktiv: z.boolean().optional(),
})
    .strict();
/**
 * Die Warnung zu einem Ziel — heute genau eine, und sie ist keine Ablehnung
 * (`D-7`).
 *
 * `unverschluesselt` heisst: Die Adresse spricht `http://`. Der Auftraggeber
 * hat `http://beta.famesystems.de/…` genannt, und ein Verbot machte die Ära am
 * Tag der Landung unbrauchbar. Was die Warnung sagt, ist das, was fehlt: Die
 * Signatur schützt die **Echtheit**, sie verbirgt nicht den Betrag und nicht
 * die Kundennummer.
 *
 * **Eine Aufzählung und keine freie Zeichenkette**, obwohl heute nur ein Wert
 * darin steht: Die Oberfläche färbt und formuliert je Art, und ein Satz aus
 * dem Server wäre ein Satz, den niemand übersetzen und niemand gestalten kann.
 * Der Text steht in der Oberfläche, die Art hier.
 */
export const webhookZielwarnungSchema = z.enum(['unverschluesselt']);
/**
 * Eine Zeile der Endpunktliste (Spec 7.3, Zeile „Liste").
 *
 * **Der Endpunkt und vier Zahlen daneben, nicht in ihm.**
 * {@link webhookEndpunktSchema} ist die Auskunft über die ZEILE in
 * `webhook_endpoint`; was hier dazukommt, ist gerechnet und steht in einer
 * anderen Tabelle. Sie in dasselbe Objekt zu mischen hiesse, ein Schema zu
 * haben, das je nach Aufrufweg halb gefüllt ist.
 *
 * **`letzterErfolgAm` ist `null`, solange nie eine Zustellung durchkam** — und
 * das unterscheidet einen frisch angelegten Endpunkt nicht von einem, der seit
 * Wochen scheitert. Deshalb steht `erschoepft` daneben: erst beide zusammen
 * beantworten „läuft das?".
 */
export const webhookEndpunktZeileSchema = z
    .object({
    endpunkt: webhookEndpunktSchema,
    /** Genau dann gesetzt, wenn die Adresse eine Warnung trägt — sonst `null`. */
    warnung: webhookZielwarnungSchema.nullable(),
    /** `zugestellt_am` der jüngsten erfolgreichen Zustellung, sonst `null`. */
    letzterErfolgAm: utcZeitpunktSchema.nullable(),
    offen: z.number().int().nonnegative(),
    zugestellt: z.number().int().nonnegative(),
    erschoepft: z.number().int().nonnegative(),
})
    .strict();
/** Die Endpunktliste — aktive und deaktivierte, älteste zuerst. */
export const webhookEndpunktListeSchema = z
    .object({ endpunkte: z.array(webhookEndpunktZeileSchema) })
    .strict();
/**
 * Die Antwort auf eine Änderung — der Endpunkt und seine Warnung, **ohne
 * Secret**.
 *
 * **Eine eigene Form und nicht `webhookEndpunktSchema` allein**, weil die
 * Warnung an der ADRESSE hängt und nicht an der Zeile: Wer die URL von
 * `https` auf `http` ändert, will die Warnung in derselben Antwort sehen, in
 * der die Änderung bestätigt wird, und nicht erst beim nächsten Laden der
 * Liste.
 *
 * **Und ausdrücklich nicht {@link webhookEndpunktMitSecretSchema}.** Ein
 * Ändern gibt kein Secret zurück; die zwei Formen unterscheiden sich in genau
 * diesem einen Feld, und dass sie sich unterscheiden, ist die Zusage
 * (`E-1206`).
 */
export const webhookEndpunktAntwortSchema = z
    .object({
    endpunkt: webhookEndpunktSchema,
    warnung: webhookZielwarnungSchema.nullable(),
})
    .strict();
/**
 * **Die einzige Antwortform dieses Systems, die ein Secret im Klartext
 * trägt** — die Antwort auf das Anlegen und auf die Rotation (`D-6`,
 * Spec 7.2, `E-1206`).
 *
 * Sie hat einen eigenen Namen, damit „hier steht ein Geheimnis drin" eine
 * Eigenschaft des TYPS ist und nicht eine Eigenschaft eines Aufrufs. Wer eine
 * dritte Stelle sucht, an der ein Secret herauskommt, sucht nach diesem
 * Schema — und findet genau zwei Fundstellen.
 *
 * **Es gibt keinen Endpunkt, der ein GESPEICHERTES Secret zurückgibt**, auch
 * nicht für den Administrator, auch nicht mit einer zweiten Fähigkeit. Der
 * Klartext existiert im Server genau in dem Aufruf, der ihn erzeugt; danach
 * steht in der Ablage nur der Geheimtext, und den zurückzuentschlüsseln tut
 * allein der Zusteller, um zu signieren.
 */
export const webhookEndpunktMitSecretSchema = z
    .object({
    endpunkt: webhookEndpunktSchema,
    warnung: webhookZielwarnungSchema.nullable(),
    /**
     * Der Klartext, 32 Byte base64url. **Dies ist die einzige Anzeige** — die
     * Oberfläche schreibt genau diesen Satz daneben.
     */
    secret: z.string().min(1),
})
    .strict();
/**
 * Die Seite des Zustellungsprotokolls (Spec 7.3, Zeile „Zustellungsprotokoll").
 *
 * **`gesamt` zählt an `limit` vorbei und NACH dem Filter**, `gesamtOhneFilter`
 * daneben — dieselbe Doppelangabe wie bei `systemAnfragenListeSchema` und aus
 * demselben Grund (Handbuch §3.132): Eine Zahl neben einem gesetzten Filter
 * liest sich sonst als Gesamtzahl und ist keine. `gesamtOhneFilter` ist dabei
 * die Zahl über **diesen einen Endpunkt** und nicht über die Tabelle — was
 * ein Nachbarendpunkt an Zeilen trägt, geht diese Ansicht nichts an.
 */
export const webhookZustellungenSeiteSchema = z
    .object({
    zeilen: z.array(webhookZustellungSchema),
    gesamt: z.number().int().nonnegative(),
    gesamtOhneFilter: z.number().int().nonnegative(),
    /** Die Blättergrösse, die wirklich gegolten hat (nach Vorgabe und Deckel). */
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=webhooks.js.map